import { useState, useEffect } from 'react';
import { subscriptionApi } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  CreditCard,
  Gift,
  RotateCcw,
  DollarSign,
  Crown,
  User,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface Subscription {
  id: string;
  userId: string;
  username: string;
  email: string;
  plan: 'free' | 'pro' | 'enterprise';
  price: number;
  currency: string;
  startedAt: string;
  renewAt?: string;
  status: 'active' | 'cancelled' | 'expired';
  provider: string;
  providerCustomerId?: string;
}

interface Payment {
  id: string;
  userId: string;
  username: string;
  amount: number;
  currency: string;
  status: 'success' | 'failed' | 'pending' | 'refunded';
  provider: string;
  providerPaymentId: string;
  createdAt: string;
}

export function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [totalSubs, setTotalSubs] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'subscriptions' | 'payments'>('subscriptions');
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({
    userId: '',
    plan: 'pro',
    durationMonths: 1,
    reason: '',
  });

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    try {
      const response = await subscriptionApi.listSubscriptions({
        page,
        pageSize,
        status: status || undefined,
        plan: plan || undefined,
      });
      if (response.code === 0) {
        setSubscriptions(response.data.items as Subscription[]);
        setTotalSubs(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const response = await subscriptionApi.listPayments({
        page,
        pageSize,
      });
      if (response.code === 0) {
        setPayments(response.data.items as Payment[]);
        setTotalPayments(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    } else {
      fetchPayments();
    }
  }, [page, pageSize, status, plan, activeTab]);

  const handleGrantSubscription = async () => {
    try {
      await subscriptionApi.grantSubscription(grantForm.userId, {
        plan: grantForm.plan,
        durationMonths: grantForm.durationMonths,
        reason: grantForm.reason,
      });
      setShowGrantModal(false);
      fetchSubscriptions();
    } catch (err) {
      console.error('Failed to grant subscription:', err);
    }
  };

  const handleExtendSubscription = async (subscriptionId: string, months: number) => {
    try {
      await subscriptionApi.extendSubscription(subscriptionId, months);
      fetchSubscriptions();
    } catch (err) {
      console.error('Failed to extend subscription:', err);
    }
  };

  const handleRefund = async (paymentId: string) => {
    if (!confirm('确定要退款吗？')) return;
    try {
      await subscriptionApi.processRefund(paymentId, 'Admin refund');
      fetchPayments();
    } catch (err) {
      console.error('Failed to process refund:', err);
    }
  };

  const formatCurrency = (amount: number, currency?: string) => {
    const safeCurrency = currency?.toUpperCase() || 'CNY';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: safeCurrency,
    }).format(amount || 0);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'free':
        return <Badge variant="default">免费版</Badge>;
      case 'pro':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-600">
            <Crown className="w-3 h-3" />
            专业版
          </span>
        );
      case 'enterprise':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
            <Crown className="w-3 h-3" />
            企业版
          </span>
        );
      default:
        return <Badge>{plan}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">生效中</Badge>;
      case 'cancelled':
        return <Badge variant="warning">已取消</Badge>;
      case 'expired':
        return <Badge variant="error">已过期</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="success">成功</Badge>;
      case 'failed':
        return <Badge variant="error">失败</Badge>;
      case 'pending':
        return <Badge variant="warning">处理中</Badge>;
      case 'refunded':
        return <Badge variant="info">已退款</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">订阅与支付</h1>
          <p className="text-zinc-500 mt-1">管理用户订阅和支付记录</p>
        </div>
        <Button onClick={() => setShowGrantModal(true)}>
          <Gift className="w-4 h-4" />
          赠送订阅
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200">
        <nav className="flex gap-6">
          {[
            { key: 'subscriptions', label: '订阅列表', icon: CreditCard },
            { key: 'payments', label: '支付记录', icon: DollarSign },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.key
                  ? 'border-accent-600 text-accent-600'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {activeTab === 'subscriptions' ? (
              <>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
                >
                  <option value="">所有状态</option>
                  <option value="active">生效中</option>
                  <option value="cancelled">已取消</option>
                  <option value="expired">已过期</option>
                </select>
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
                >
                  <option value="">所有套餐</option>
                  <option value="free">免费版</option>
                  <option value="pro">专业版</option>
                  <option value="enterprise">企业版</option>
                </select>
              </>
            ) : (
              <select
                className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
              >
                <option value="">所有状态</option>
                <option value="success">成功</option>
                <option value="failed">失败</option>
                <option value="refunded">已退款</option>
              </select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>{activeTab === 'subscriptions' ? '订阅列表' : '支付记录'}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : activeTab === 'subscriptions' ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>用户</TableHead>
                    <TableHead>套餐</TableHead>
                    <TableHead>价格</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>开始时间</TableHead>
                    <TableHead>到期时间</TableHead>
                    <TableHead>支付方式</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-zinc-900">{sub.username}</p>
                          <p className="text-sm text-zinc-500">{sub.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(sub.plan)}</TableCell>
                      <TableCell>{formatCurrency(sub.price, sub.currency)}</TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      <TableCell>{formatDate(sub.startedAt)}</TableCell>
                      <TableCell>{formatDate(sub.renewAt)}</TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-600">{sub.provider}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <select
                            className="text-sm border border-zinc-300 rounded-lg px-2 py-1"
                            onChange={(e) => {
                              const months = parseInt(e.target.value);
                              if (months > 0) {
                                handleExtendSubscription(sub.id, months);
                              }
                            }}
                            defaultValue=""
                          >
                            <option value="" disabled>延长订阅</option>
                            <option value="1">1个月</option>
                            <option value="3">3个月</option>
                            <option value="6">6个月</option>
                            <option value="12">12个月</option>
                          </select>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(totalSubs / pageSize)}
                onPageChange={setPage}
                totalItems={totalSubs}
                pageSize={pageSize}
              />
            </>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>支付 ID</TableHead>
                    <TableHead>用户</TableHead>
                    <TableHead>金额</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>支付方式</TableHead>
                    <TableHead>支付时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-mono text-sm">{payment.id.slice(0, 8)}...</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{payment.username}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(payment.amount, payment.currency)}
                      </TableCell>
                      <TableCell>{getPaymentStatusBadge(payment.status)}</TableCell>
                      <TableCell>{payment.provider}</TableCell>
                      <TableCell>{formatDate(payment.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        {payment.status === 'success' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRefund(payment.id)}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            退款
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(totalPayments / pageSize)}
                onPageChange={setPage}
                totalItems={totalPayments}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Grant Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-zinc-900 mb-4">赠送订阅</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">用户 ID</label>
                <input
                  type="text"
                  value={grantForm.userId}
                  onChange={(e) => setGrantForm({ ...grantForm, userId: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                  placeholder="输入用户 ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">套餐</label>
                <select
                  value={grantForm.plan}
                  onChange={(e) => setGrantForm({ ...grantForm, plan: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                >
                  <option value="pro">专业版</option>
                  <option value="enterprise">企业版</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">时长（月）</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={grantForm.durationMonths}
                  onChange={(e) => setGrantForm({ ...grantForm, durationMonths: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">原因（可选）</label>
                <textarea
                  value={grantForm.reason}
                  onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300"
                  rows={3}
                  placeholder="赠送原因..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={() => setShowGrantModal(false)}>
                取消
              </Button>
              <Button onClick={handleGrantSubscription}>
                确认赠送
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
